import {appRouter} from '@app/app-router';
import {CustomPage} from '@app/gen/schemas/custom-page';
import {GetShareableLinkPageData200} from '@app/gen/schemas/get-shareable-link-page-data200';
import {ListProducts200} from '@app/gen/schemas/list-products200';
import {BaseBackendBootstrapData} from '@common/core/base-backend-bootstrap-data';
import {CommonProvider} from '@common/core/common-provider';
import {BaseBackendSettings} from '@common/core/settings/base-backend-settings';
import {ignoredSentryErrors} from '@common/http/errors/ignored-sentry-errors';
import {clearChunkReloadFlag, installChunkLoadRecovery} from '@common/core/retry-lazy-import';
import {SectionConfig} from '@common/ui/landing-page/landing-page-config';
import {getBootstrapData} from '@ui/bootstrap-data/bootstrap-data-store';
import {ensureActiveLocaleLines} from '@ui/i18n/locale-lines-store';
import {rootEl} from '@ui/root-el';
import {createRoot, RootOptions} from 'react-dom/client';
import './app.css';

declare module '@ui/settings/settings' {
  interface Settings extends BaseBackendSettings {
    homepage: {
      type: 'loginPage' | 'landingPage';
    };
    landingPage?: {
      sections: SectionConfig[];
      /** Language of the default text (build 130). */
      baseLocale?: string;
    };
    /** Landing editor preview only (never saved). */
    landingPreview?: {locale?: string; dir?: 'ltr' | 'rtl'};
    drive?: {
      details_default_visibility: boolean;
      default_view: 'list' | 'grid';
      send_share_notification: boolean;
      send_file_request_notification: boolean;
      default_available_space: number;
      direct_links: boolean;
      copy_link_default: 'shareable' | 'direct';
      folder_color?: string;
      icon_tint?: boolean;
      trash_keep?: '7' | '30' | 'custom' | 'unlimited';
      trash_keep_days?: number;
      trash_require_approval?: boolean;
      send_delete_request_notification?: boolean;
    };
    share?: {
      suggest_emails: boolean;
    };
    ads?: {
      drive?: string;
      'file-preview'?: string;
      'landing-top'?: string;
      disable?: boolean;
    };
  }
}

declare module '@ui/bootstrap-data/bootstrap-data' {
  interface BootstrapData extends BaseBackendBootstrapData {
    loaders?: {
      landingPage?: {
        products: ListProducts200;
        sections?: SectionConfig[];
        baseLocale?: string;
      };
      customPage?: {data: CustomPage};
      shareableLinkPage?: GetShareableLinkPageData200;
    };
  }
}

installChunkLoadRecovery();
clearChunkReloadFlag();

async function mountApp() {
  // Build 131: translations load in parallel (started in <head>), render waits for them.
  const linesReady = ensureActiveLocaleLines();
  const data = getBootstrapData();
  let options: RootOptions | undefined = undefined;
  const sentryDsn = data.settings.logging.sentry_public;
  if (sentryDsn && import.meta.env.PROD) {
    try {
      const Sentry = await import('@sentry/react');
      Sentry.init({
        dsn: sentryDsn,
        integrations: [Sentry.browserTracingIntegration()],
        tracesSampleRate: 0.2,
        ignoreErrors: ignoredSentryErrors,
        release: data.sentry_release,
      });

      options = {
        onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
          console.warn('Uncaught error', error, errorInfo.componentStack);
        }),
        onCaughtError: Sentry.reactErrorHandler(),
        onRecoverableError: Sentry.reactErrorHandler(),
      };
    } catch (error) {
      // Optional monitoring must never prevent the application from rendering.
      console.warn('Unable to initialize error reporting', error);
    }
  }

  await linesReady;
  const app = <CommonProvider router={appRouter} />;

  createRoot(rootEl, options).render(app);
}

void mountApp();
